import { useEffect, useSyncExternalStore } from 'react';
import {
  apiService, FEATURE_NOT_ENABLED, FEATURE_NOT_ENABLED_EVENT, FEATURE_NOT_ENABLED_TEXT,
} from '../services/apiService';

/**
 * Feature access: which of the gated pages this account may open.
 *
 * The new For You page, What's New and OCEAN are switched per feature by a
 * super admin in the admin panel's Feature access tabs (For You & What's New
 * → Feature access, Ocean → Feature access; Backend/feature_access.py,
 * app_settings 'feature_access'), one of:
 *   superadmins  (default) super admins only — "Super admin preview" badge
 *   selected     super admins plus the admins picked there — "Preview" badge
 *   admins       every admin — "Preview" badge
 *   everyone     every signed-in account — no badge
 * The server decides; this file only asks it, once after sign-in, through
 * GET /api/v1/feature-access:
 *
 *   {success: true, features: {foryou|whatsnew|ocean:
 *     {enabled: bool, mode: FeatureMode, preview: bool}}}
 *
 * `preview` = enabled while the mode is not "everyone" (the page is still
 * limited to some admins). Until the answer arrives — and when the call fails,
 * or an older server has no such route — every gated feature counts as NOT
 * enabled, so a member never sees a gated page flash and the legacy pages stay
 * in place.
 *
 * An ask that has not answered after ASK_TIMEOUT_MS counts as failed, so the
 * For You and Ocean views (which wait for the answer) fall back to the legacy
 * page / Coming Soon instead of spinning.
 *
 * It asks again:
 *   - when a super admin saves a mode (announceFeatureAccessChange — this tab
 *     directly, other tabs of the same browser through a localStorage ping);
 *   - when a gated API answers 403 feature_not_enabled (this tab's answer is
 *     out of date, e.g. the feature went back to super admins): apiService fires
 *     FEATURE_NOT_ENABLED_EVENT, page fetchers call featureNotEnabled(); App
 *     then swaps to the legacy page, home or Coming Soon;
 *   - when the tab becomes visible again after a failed ask, or once the last
 *     answer is older than STALE_MS.
 * The server reads the stored modes for this route (not a per-worker cache),
 * so a re-ask right after a toggle gets the new answer.
 */

export type FeatureKey = 'foryou' | 'whatsnew' | 'ocean';
export const FEATURE_KEYS: readonly FeatureKey[] = ['foryou', 'whatsnew', 'ocean'];
export type FeatureMode = 'superadmins' | 'selected' | 'admins' | 'everyone';
export const FEATURE_MODES: readonly FeatureMode[] = ['superadmins', 'selected', 'admins', 'everyone'];

/** How the Feature access tabs name each mode. */
export const FEATURE_MODE_LABELS: Record<FeatureMode, string> = {
  superadmins: 'Super admins only',
  selected: 'Selected admins',
  admins: 'All admins',
  everyone: 'Everyone',
};

/** Unknown or missing = the default, super admins only. Old stored values
 *  ("admins" / "everyone") keep their meaning. */
export const featureModeOf = (v: any): FeatureMode =>
  (FEATURE_MODES.includes(v) ? v as FeatureMode : 'superadmins');

export interface FeatureState {
  enabled: boolean;
  mode: FeatureMode;
  /** Enabled while still limited to some admins (mode is not "everyone"). */
  preview: boolean;
}
export type FeatureMap = Record<FeatureKey, FeatureState>;

export interface FeatureAccess {
  /** idle: not asked yet · loading · ready · failed (everything closed). */
  status: 'idle' | 'loading' | 'ready' | 'failed';
  /** The server has answered (or failed) for the signed-in account. */
  loaded: boolean;
  features: FeatureMap;
}

/** Labels as the admin card shows them (the server sends its own too). */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  foryou: 'For You (new)',
  whatsnew: "What's New",
  ocean: 'OCEAN',
};

/** The deep link that opens each feature's page in the app (App.tsx DEEPLINK_LANDING). */
export const FEATURE_OPEN_URL: Record<FeatureKey, string> = {
  foryou: '/?view=foryou',
  whatsnew: '/?view=whatsnew',
  ocean: '/?view=ocean',
};

const CLOSED: FeatureState = Object.freeze({ enabled: false, mode: 'superadmins', preview: false }) as FeatureState;
export const CLOSED_FEATURES: FeatureMap = Object.freeze({
  foryou: CLOSED, whatsnew: CLOSED, ocean: CLOSED,
}) as FeatureMap;

const UNLOADED: FeatureAccess = Object.freeze({ status: 'idle', loaded: false, features: CLOSED_FEATURES }) as FeatureAccess;

/** Read the server's answer. Anything unexpected for a feature = closed. */
export function parseFeatureAccess(body: any): FeatureMap {
  const src = body && typeof body === 'object' ? body.features : null;
  const out: FeatureMap = { ...CLOSED_FEATURES };
  if (!src || typeof src !== 'object') return out;
  for (const k of FEATURE_KEYS) {
    const f = src[k];
    if (!f || typeof f !== 'object') continue;
    const enabled = f.enabled === true;
    const mode = featureModeOf(f.mode);
    out[k] = {
      enabled,
      mode,
      preview: enabled && mode !== 'everyone' && f.preview !== false,
    };
  }
  return out;
}

const storedUid = (): string => {
  try { return apiService.getStoredUserId() || ''; } catch { return ''; }
};

/** An ask with no answer by then counts as failed (everything closed). */
const ASK_TIMEOUT_MS = 8000;
/** Re-ask on returning to the tab once the last answer is this old. */
const STALE_MS = 5 * 60 * 1000;
/** At most one re-ask per this long from 403 feature_not_enabled answers (a
 *  page may fire several calls at once). */
const NOT_ENABLED_GAP_MS = 3000;

let snap: FeatureAccess = UNLOADED;
/** The account `snap` belongs to; a different signed-in account sees UNLOADED. */
let snapUid = '';
let seq = 0;
/** When the server last answered (Date.now()); 0 = never. */
let answeredAt = 0;
let notEnabledAt = 0;
const listeners = new Set<() => void>();

const setSnap = (next: FeatureAccess) => {
  snap = next;
  listeners.forEach(l => l());
};

/** One ask: the parsed answer, or null on any failure, a bad body or no
 *  answer within ASK_TIMEOUT_MS (a late answer is dropped). */
const askServer = (uid: string): Promise<FeatureMap | null> => new Promise(resolve => {
  let done = false;
  const finish = (v: FeatureMap | null) => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    resolve(v);
  };
  const timer = setTimeout(() => finish(null), ASK_TIMEOUT_MS);
  apiService.getFeatureAccess(uid).then(r => {
    const body: any = r.success ? r.data : null;
    finish(body && body.success !== false && body.features && typeof body.features === 'object'
      ? parseFeatureAccess(body) : null);
  }, () => finish(null));
});

/** Ask the server now. Resolves with the new state (never throws). */
export async function refreshFeatureAccess(): Promise<FeatureAccess> {
  const uid = storedUid();
  const mine = ++seq;
  if (!uid) {
    snapUid = '';
    setSnap(UNLOADED);
    return snap;
  }
  if (uid !== snapUid) {
    // Another account (or the first ask): forget whatever was known before.
    snapUid = uid;
    setSnap({ status: 'loading', loaded: false, features: CLOSED_FEATURES });
  } else if (!snap.loaded) {
    setSnap({ ...snap, status: 'loading' });
  }
  const features = await askServer(uid);
  if (mine !== seq || snapUid !== uid) return snap; // a newer ask owns the state
  if (features) {
    answeredAt = Date.now();
    setSnap({ status: 'ready', loaded: true, features });
  }
  // A failed re-ask keeps the last good answer for the same account; a failed
  // first ask leaves everything closed.
  else setSnap(snap.status === 'ready' ? snap : { status: 'failed', loaded: true, features: CLOSED_FEATURES });
  return snap;
}

const PING_KEY = 'utech_feature_access_ping';
export const FEATURE_ACCESS_CHANGED_EVENT = 'utech:feature-access-changed';

/** Call after an admin changes a mode: re-ask here and in this browser's other tabs. */
export function announceFeatureAccessChange(): void {
  void refreshFeatureAccess();
  try { localStorage.setItem(PING_KEY, String(Date.now())); } catch { /* storage blocked */ }
  try { window.dispatchEvent(new Event(FEATURE_ACCESS_CHANGED_EVENT)); } catch { /* old browser */ }
}

/** For a page's own fetch helper (components/foryou/api.ts, ocean/api.ts):
 *  true when the answer is the server's 403 feature_not_enabled — this tab's
 *  feature access is out of date, so it is asked again and App swaps the page
 *  for its fallback. Show FEATURE_NOT_ENABLED_TEXT meanwhile, not the code.
 *  (apiService.makeRequest does the same for calls that go through it.) */
export function featureNotEnabled(status: number, body: any): boolean {
  if (status !== 403 || body?.detail !== FEATURE_NOT_ENABLED) return false;
  try { window.dispatchEvent(new Event(FEATURE_NOT_ENABLED_EVENT)); } catch { /* not in a browser */ }
  return true;
}
export { FEATURE_NOT_ENABLED_TEXT };

let wired = false;
const wireGlobalListeners = () => {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener('storage', e => {
    if (e.key === PING_KEY && snapUid) void refreshFeatureAccess();
  });
  window.addEventListener(FEATURE_NOT_ENABLED_EVENT, () => {
    const now = Date.now();
    if (!snapUid || now - notEnabledAt < NOT_ENABLED_GAP_MS) return;
    notEnabledAt = now;
    void refreshFeatureAccess();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !storedUid() || snap.status === 'loading') return;
    if (snap.status === 'failed' || (snap.status === 'ready' && Date.now() - answeredAt > STALE_MS)) {
      void refreshFeatureAccess();
    }
  });
};

const subscribe = (l: () => void) => {
  wireGlobalListeners();
  listeners.add(l);
  return () => { listeners.delete(l); };
};

const getSnapshot = (): FeatureAccess => (storedUid() === snapUid ? snap : UNLOADED);

/** The signed-in account's feature access; asks the server on first use. */
export function useFeatureAccess(): FeatureAccess {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    if (snap.status === 'idle' || storedUid() !== snapUid) void refreshFeatureAccess();
  }, []);
  return s;
}
